import React, { useContext } from 'react';
import { Box, Button, useTheme } from '@mui/material';
import useDossierDocuments, {
    STATUS_REFUSED,
    STATUS_VALID,
} from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useDossierDocuments';
import { useDocumentPreviewContext } from '@europrocurement/l2d-modules/modules/Dossiers/providers/DocumentPreviewContext';
import { useDossierDocumentsFiles } from '@europrocurement/l2d-modules/modules/Dossiers/hooks';
import {
    DocumentContext,
    SelectedDossierContext,
} from '@europrocurement/l2d-modules/modules/Dossiers/providers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileDashedLine, faRefresh } from '@fortawesome/pro-regular-svg-icons';
import DossierDocumentsValidationDocumentName from '../DossierDocumentsValidationDocumentName/DossierDocumentsValidationDocumentName';
import DossierDocumentsValidationDocumentStatus from '../DossierDocumentsValidationDocumentStatus/DossierDocumentsValidationDocumentStatus';
import DossierDocumentValidationDocumentType from '../../DossierDocumentValidationDocumentType/DossierDocumentValidationDocumentType';

export type DossierDocumentsValidationHeaderProps = {
    onDocumentTypeChange: (fichierId: number, typeDocId: number) => void;
    onDocumentStatusChange: (fichierId: number, statusId: number) => void;
    onDocumentDeliassage: (fichierId: number) => void;
    onDocumentRefresh?: () => void;
    navigationBtn?: React.JSX.Element;
};

const DossierDocumentsValidationHeader: React.FunctionComponent<DossierDocumentsValidationHeaderProps> =
    function (props) {
        const {
            onDocumentTypeChange,
            onDocumentStatusChange,
            onDocumentDeliassage,
            onDocumentRefresh,
            navigationBtn,
        } = props;
        const { getDocumentTypes } = useDossierDocumentsFiles();
        const { getStatusIdentifier, getColorByStatus, getFileIcon, postDossierTypeDocuments } =
            useDossierDocuments();
        const { dossier } = useContext(SelectedDossierContext);
        const { addDocument } = useContext(DocumentContext);
        const { documentPreview, setDocumentPreview } = useDocumentPreviewContext();
        const { palette } = useTheme();
        const documentColor = documentPreview?.statusId
            ? getColorByStatus(getStatusIdentifier(documentPreview?.statusId))
            : 'primary';
        const documentIcon = documentPreview?.fileType
            ? getFileIcon(documentPreview?.fileType)
            : 'file';

        const handleAccept = () => {
            if (!documentPreview?.fichierId) {
                return;
            }

            onDocumentStatusChange(documentPreview.fichierId as number, STATUS_VALID);
            setDocumentPreview({
                ...documentPreview,
                statusId: STATUS_VALID,
            });
        };

        const handleDeny = () => {
            if (!documentPreview?.fichierId) {
                return;
            }

            onDocumentStatusChange(documentPreview.fichierId as number, STATUS_REFUSED);
            setDocumentPreview({
                ...documentPreview,
                statusId: STATUS_REFUSED,
            });
        };

        return (
            <Box
                sx={{
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    padding: '15px 16px',
                    borderBottomColor: palette.grey[200],
                    borderBottomWidth: '1px',
                    borderBottomStyle: 'solid',
                }}
            >
                {navigationBtn}
                {documentPreview && (
                    <DossierDocumentsValidationDocumentName
                        fileIcon={documentIcon}
                        documentColor={documentColor}
                        label={documentPreview.documentLabel as string}
                        fileName={documentPreview.fileName as string}
                    />
                )}
                {documentPreview && (
                    <DossierDocumentValidationDocumentType
                        label={documentPreview.documentLabel}
                        typeDocType={documentPreview.expediteurType}
                        onDocumentTypeChange={async (newDocType) => {
                            if (!newDocType) {
                                return;
                            }
                            const existingDosDocs = getDocumentTypes(
                                documentPreview.dossierId as number,
                                documentPreview.expediteurType,
                                documentPreview.documentTypeId,
                            );
                            const dosDoc = existingDosDocs.find(
                                (existingDosDoc) =>
                                    existingDosDoc.typeDoc &&
                                    existingDosDoc.typeDoc === newDocType.id,
                            );

                            // This type of document do not extist in dossier
                            if (!dosDoc) {
                                // Add a dosDoc to the dossier
                                // Link document type to the dossier
                                const newDosDoc = await postDossierTypeDocuments(
                                    dossier.id as number,
                                    newDocType.id as number,
                                    newDocType.label as string,
                                    dossier.statutId as number,
                                );

                                // Maj document in context
                                addDocument(newDosDoc);
                                onDocumentTypeChange(
                                    documentPreview.fichierId as number,
                                    newDosDoc.id as number,
                                );
                                setDocumentPreview({
                                    ...documentPreview,
                                    documentLabel: newDocType.label,
                                    documentTypeId: newDosDoc.id,
                                });
                            } else {
                                onDocumentTypeChange(
                                    documentPreview.fichierId as number,
                                    dosDoc.id,
                                );
                                setDocumentPreview({
                                    ...documentPreview,
                                    documentLabel: newDocType.label,
                                    documentTypeId: dosDoc.id,
                                });
                            }
                        }}
                    />
                )}
                <Box
                    sx={{
                        display: 'flex',
                        gap: '0 8px',
                        alignItems: 'center',
                        marginLeft: 'auto',
                        marginRight: '0',
                    }}
                >
                    {documentPreview && (
                        <DossierDocumentsValidationDocumentStatus
                            statusId={documentPreview?.statusId as number}
                            onAcceptDocument={handleAccept}
                            onDenyDocument={handleDeny}
                        />
                    )}

                    {documentPreview?.fichierId && documentPreview.fichierId && (
                        <Button
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0 8px',
                                color: palette.primary.dark,
                                ':hover': {
                                    color: palette.primary.main,
                                },
                            }}
                            onClick={() =>
                                onDocumentDeliassage(documentPreview.fichierId as number)
                            }
                        >
                            <FontAwesomeIcon
                                icon={faFileDashedLine}
                                fontSize="18px"
                            />
                            Déliasser
                        </Button>
                    )}
                    {onDocumentRefresh && typeof onDocumentRefresh !== 'undefined' && (
                        <Button
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0 8px',
                                color: palette.primary.dark,
                                ':hover': {
                                    color: palette.primary.main,
                                },
                            }}
                            onClick={() => onDocumentRefresh()}
                        >
                            <FontAwesomeIcon
                                icon={faRefresh}
                                fontSize="18px"
                            />
                        </Button>
                    )}
                </Box>
            </Box>
        );
    };

export default DossierDocumentsValidationHeader;
